/**
 * Client for the QuickRad Scanner Agent — a Windows tray app that exposes
 * document scanners over loopback HTTP at 127.0.0.1:47821.
 *
 * Plain JS, no Vue dependency. Every failure is normalised into the shape
 * described by {@link AgentError} so callers never branch on raw fetch
 * rejections or HTTP codes.
 *
 * Deliberately uses bare `fetch` rather than the project's Axios helpers: the
 * agent's CORS policy allows only the `content-type` request header, so any
 * interceptor that injects Authorization or tracing headers would fail the
 * preflight and the request would never reach the agent.
 */

const PRODUCTION_AGENT_BASE_URL = "http://127.0.0.1:47821/api/v1";

function normalizeBaseUrl(url) {
  return String(url || "").replace(/\/+$/, "");
}

function joinAgentPath(path) {
  return `${AGENT_BASE_URL}/${String(path || "").replace(/^\/+/, "")}`;
}

export const AGENT_BASE_URL = normalizeBaseUrl(
  process.env.VUE_APP_AGENT_BASE_URL ||
    (process.env.NODE_ENV === "development"
      ? "/scanner-agent"
      : PRODUCTION_AGENT_BASE_URL)
);

/** Agent builds older than this lack behaviour the client depends on. */
export const MIN_AGENT_VERSION = "1.0.0";

/**
 * The agent accepts any https page on aikenist.com or a subdomain of it, at any
 * depth. Keep this in step with IsAllowedOrigin in the agent's AgentHost.cs: the
 * leading dot is what keeps a lookalike like notaikenist.com out.
 */
export const ALLOWED_ORIGIN_DESCRIPTION = "an https page on aikenist.com";

export function isAllowedOrigin(origin) {
  try {
    const url = new URL(origin);
    return (
      url.protocol === "https:" &&
      (url.hostname === "aikenist.com" || url.hostname.endsWith(".aikenist.com"))
    );
  } catch {
    return false;
  }
}

/**
 * Chromium 152+ gates a public HTTPS page reaching 127.0.0.1 behind the
 * `local-network-access` permission. The agent's
 * `Access-Control-Allow-Private-Network` reply is the older Private Network
 * Access mechanism, which that permission supersedes — the header alone no
 * longer gets the request sent.
 *
 * The prompt is only shown for a request made with transient user activation.
 * A probe fired from a component's mounted() hook has none, so the browser
 * denies it without asking and `fetch` rejects exactly as it would if nothing
 * were listening. That is why this has to be read before probing rather than
 * inferred from the failure.
 *
 * A page served from localhost is already in the local address space, so none
 * of this applies in development.
 *
 * @returns {Promise<'granted'|'prompt'|'denied'|'unknown'>} `unknown` when the
 *   browser does not implement the permission, in which case nothing gates the
 *   request and the caller should just probe.
 */
export async function localNetworkPermission() {
  try {
    if (typeof navigator === "undefined" || !navigator.permissions) return "unknown";
    const status = await navigator.permissions.query({ name: "local-network-access" });
    return status && status.state ? status.state : "unknown";
  } catch (e) {
    // Browsers without the permission throw on the unrecognised name.
    return "unknown";
  }
}

/**
 * Installers are published from one host rather than each deployment's own
 * origin, so a centre on gtr.aikenist.com still gets a build that exists.
 */
const AGENT_DOWNLOAD_BASE = "https://qr4.aikenist.com/downloads";

/**
 * Filenames as published, keyed by platform. The agent is a native app — the
 * Windows build cannot install on a Mac — so handing every operator the .exe
 * is a dead end they cannot diagnose from the download itself.
 *
 * These are the two strings to change if the published names change.
 */
const AGENT_INSTALLERS = {
  windows: "QuickRadScannerAgent-setup.exe",
  mac: "QuickRadScannerAgent-setup.pkg",
};

/**
 * macOS reports itself honestly through userAgentData; the legacy userAgent is
 * the fallback for anything that lacks it. Apple Silicon still says "Intel Mac
 * OS X" in the userAgent for compatibility, which is why architecture is not
 * read from it — the published pkg has to cover both.
 * @returns {boolean}
 */
export function isMacOS() {
  if (typeof navigator === "undefined") return false;
  const data = navigator.userAgentData;
  if (data && typeof data.platform === "string" && data.platform) {
    return data.platform === "macOS";
  }
  return /Mac/i.test(navigator.platform || navigator.userAgent || "");
}

/** The installer for the platform the operator is actually running. */
export function agentDownloadUrl() {
  const file = isMacOS() ? AGENT_INSTALLERS.mac : AGENT_INSTALLERS.windows;
  return `${AGENT_DOWNLOAD_BASE}/${file}`;
}

/** Names the platform in the download link, so a wrong build is obvious. */
export function agentPlatformLabel() {
  return isMacOS() ? "macOS" : "Windows";
}

const TIMEOUTS = {
  health: 3000,
  scanners: 30000,
  startScan: 600000,
  status: 5000,
  page: 30000,
  del: 10000,
};

/**
 * @typedef {Object} AgentError
 * @property {'agent-unreachable'|'forbidden-origin'|'agent-outdated'|'http'|'timeout'|'validation'} kind
 * @property {number|null} status
 * @property {string} message Operator-facing where the agent supplied one.
 */

/**
 * @param {AgentError['kind']} kind
 * @param {string} message
 * @param {number|null} [status]
 * @returns {Error & AgentError}
 */
function agentError(kind, message, status = null) {
  const err = new Error(message);
  err.kind = kind;
  err.status = status;
  err.isAgentError = true;
  return err;
}

/**
 * Safari blocks http://127.0.0.1 from an HTTPS page as mixed content, so the
 * agent is unreachable there no matter what. Detected up front to avoid
 * presenting a connection error the operator cannot act on.
 * @returns {boolean}
 */
export function isUnsupportedBrowser() {
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
  const isIOSWebKit = /iPad|iPhone|iPod/.test(ua);
  const isSafari =
    /Safari/i.test(ua) && !/Chrome|Chromium|CriOS|FxiOS|Edg/i.test(ua);
  return isIOSWebKit || isSafari;
}

/**
 * The origin this page is served from. Wrapped so DOM access stays in this
 * module and callers stay testable.
 * @returns {string}
 */
export function currentOrigin() {
  return typeof window !== "undefined" && window.location
    ? window.location.origin
    : "";
}

/** Compares dotted numeric versions. True when `version` >= `min`. */
export function versionAtLeast(version, min) {
  const a = String(version || "").split(".").map((n) => parseInt(n, 10) || 0);
  const b = String(min || "").split(".").map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const diff = (a[i] || 0) - (b[i] || 0);
    if (diff !== 0) return diff > 0;
  }
  return true;
}

/** Pulls the agent's operator-facing `error` string out of a failed response. */
async function readErrorMessage(response, fallback) {
  try {
    const body = await response.json();
    if (body && typeof body.error === "string" && body.error) return body.error;
  } catch (e) {
    /* not JSON — fall through */
  }
  return fallback;
}

/**
 * @param {string} path
 * @param {{method?: string, body?: Object, timeout?: number, raw?: boolean}} [options]
 * @returns {Promise<any>}
 */
async function request(path, options = {}) {
  const { method = "GET", body, timeout = TIMEOUTS.status, raw = false } = options;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeout);

  let response;
  try {
    response = await fetch(joinAgentPath(path), {
      method,
      mode: "cors",
      // Never true: the agent's CORS policy does not allow credentials, and
      // enabling them makes the browser reject every response.
      credentials: "omit",
      // Declares intent to browsers implementing Local Network Access.
      // Unknown RequestInit keys are ignored elsewhere.
      targetAddressSpace: "loopback",
      signal: controller.signal,
      // content-type is the ONLY request header the agent's CORS policy
      // allows. Adding any other header fails the preflight silently.
      headers: body ? { "Content-Type": "application/json" } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch (e) {
    if (e && e.name === "AbortError") {
      throw agentError("timeout", "The scanner agent did not respond in time.");
    }
    // fetch rejects with a TypeError when nothing is listening — this is what
    // "agent not installed" looks like, not an HTTP status.
    throw agentError(
      "agent-unreachable",
      "Scanner agent not detected on this computer."
    );
  } finally {
    clearTimeout(timer);
  }

  if (response.status === 403) {
    throw agentError(
      "forbidden-origin",
      `The scanner agent rejected this page's origin. It accepts only ${ALLOWED_ORIGIN_DESCRIPTION}.`,
      403
    );
  }

  if (response.status === 400) {
    throw agentError(
      "validation",
      await readErrorMessage(response, "The scanner rejected those settings."),
      400
    );
  }

  if (!response.ok) {
    throw agentError(
      "http",
      await readErrorMessage(
        response,
        `The scanner agent returned an error (${response.status}).`
      ),
      response.status
    );
  }

  if (raw) return response;
  if (response.status === 204) return null;
  return response.json();
}

/**
 * Reads agent health and verifies it is new enough to drive.
 * @returns {Promise<{status: string, version: string, drivers: string[]}>}
 * @throws {AgentError} `agent-outdated` when older than {@link MIN_AGENT_VERSION}.
 */
export async function health() {
  const info = await request("/health", { timeout: TIMEOUTS.health });
  if (!versionAtLeast(info && info.version, MIN_AGENT_VERSION)) {
    throw agentError(
      "agent-outdated",
      `Scanner agent ${info && info.version} is too old. Version ${MIN_AGENT_VERSION} or newer is required.`
    );
  }
  return info;
}

/**
 * Enumerates attached scanners. Expensive — it wakes USB devices and can take
 * seconds — so call it on demand, never on a timer.
 *
 * An empty array means no scanner is attached, which is a normal state. A 500
 * means enumeration itself failed and is worth retrying; it surfaces as an
 * `http` error rather than an empty list.
 * @returns {Promise<Array<{id: string, name: string, driver: string}>>}
 */
export async function listScanners() {
  const body = await request("/scanners", { timeout: TIMEOUTS.scanners });
  return (body && body.scanners) || [];
}

/**
 * Queues a scan. Returns as soon as the job is created, not when it finishes.
 * @param {{scannerId: string, dpi: 200|300, source: 'flatbed'|'feeder', colorMode: 'color'|'grayscale'}} options
 * @returns {Promise<string>} the scan id
 */
export async function startScan(options) {
  const body = await request("/scans", {
    method: "POST",
    body: options,
    timeout: TIMEOUTS.startScan,
  });
  return body.scanId;
}

/**
 * @param {string} scanId
 * @returns {Promise<{state: 'running'|'complete'|'failed', pageCount: number, error: string|null}>}
 */
export function getScanStatus(scanId) {
  return request(`/scans/${encodeURIComponent(scanId)}`, {
    timeout: TIMEOUTS.status,
  });
}

/**
 * Fetches one page as a Blob. Page numbers are 1-based.
 *
 * Must be fetched rather than pointed at with `<img src>`: the browser does
 * not send an Origin header on a plain image load, and the agent's Origin
 * check fails closed, so the image would 403.
 * @param {string} scanId
 * @param {number} pageNumber 1-based
 * @returns {Promise<Blob>}
 */
export async function getPageBlob(scanId, pageNumber) {
  const response = await request(
    `/scans/${encodeURIComponent(scanId)}/pages/${pageNumber}`,
    { timeout: TIMEOUTS.page, raw: true }
  );
  return response.blob();
}

/**
 * Cancels a running scan, or erases a finished one. The agent treats both as
 * the same call. Jobs are swept 30 minutes after finishing, but callers should
 * not rely on that.
 * @param {string} scanId
 * @returns {Promise<null>}
 */
export function deleteScan(scanId) {
  return request(`/scans/${encodeURIComponent(scanId)}`, {
    method: "DELETE",
    timeout: TIMEOUTS.del,
  });
}

// The agent also exposes `GET /scans/{id}/pdf`, which this client no longer
// calls. That endpoint can only see the pages of a single job, and a flatbed
// produces one job per sheet, so a multi-sheet document is composed in the
// browser instead — see services/pdfCompose.js.

export default {
  health,
  listScanners,
  startScan,
  getScanStatus,
  getPageBlob,
  deleteScan,
};
